import { tKey, type I18nKey } from "@/core/i18n";
import { cn } from "@/lib/utils";
import { AnnotationAvailableTypes, AnnotationType } from "@repo/schema";
import { useTranslation } from "react-i18next";

import { ALL_TYPES } from "./types";

const TYPE_LABELS = {
  rectangle: tKey("analytics:types.rectangle"),
  polygon: tKey("analytics:types.polygon"),
  classification: tKey("analytics:types.classification"),
} satisfies Record<AnnotationType, I18nKey<"analytics">>;

interface AnnotationTypeSelectorProps {
  availableTypes?: AnnotationAvailableTypes;
  selectedTypes: AnnotationType[];
  onToggle: (type: AnnotationType) => void;
}

export const AnnotationTypeSelector = ({
  availableTypes,
  selectedTypes,
  onToggle,
}: AnnotationTypeSelectorProps) => {
  const { t } = useTranslation(["analytics", "common"]);
  return (
    <div className="flex shrink-0 flex-wrap gap-2">
      {ALL_TYPES.map((type) => {
        const available = availableTypes?.[type] ?? true;
        const selected = selectedTypes.includes(type);
        const isLastSelected = selected && selectedTypes.length === 1;
        return (
          <button
            key={type}
            type="button"
            disabled={!available || isLastSelected}
            onClick={() => onToggle(type)}
            title={!available ? t("types.unavailable") : undefined}
            className={cn(
              "flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-medium transition-all",
              selected && available
                ? "border-primary/30 bg-primary/10 text-primary"
                : "border-black/10 bg-transparent text-muted-foreground opacity-50",
              (!available || isLastSelected) && "cursor-not-allowed",
            )}
          >
            {t(TYPE_LABELS[type])}
          </button>
        );
      })}
    </div>
  );
};
